import {
  Badge,
  Button,
  Card,
  ChannelBadge,
  ChannelFilter,
  ChannelNotice,
  EmptyState,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Spinner,
  useToast,
} from "@forge/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Scale } from "lucide-react";
import { useState } from "react";
import { ClaimReturnButton } from "../../components/claim-return-button";
import { ClaimThread } from "../../components/claim-thread";
import { ListSearch } from "../../components/list-search";
import { MobileInboxPane } from "../../components/mobile-inbox-pane";
import { PageHeader } from "../../components/page-header";
import { api, unwrap } from "../../lib/api";
import { useChannels } from "../../lib/channel";
import { matchesListQuery } from "../../lib/list-query";
import { replyVarsFromOrder } from "../../lib/reply-templates";
import { formatDateTime } from "../../lib/format";
import { isNarrowViewport } from "../../lib/mobile-title";
import {
  CLAIM_STATUS_LABELS,
  CLAIM_STATUS_VARIANTS,
  CLAIM_TYPE_LABELS,
} from "../../lib/order-status";

type ClaimsSearch = {
  status?: "opened" | "closed";
  accountId?: string;
  provider?: "mercadolivre" | "shopee";
  open?: string;
  q?: string;
};

export const Route = createFileRoute("/_app/claims")({
  validateSearch: (search: Record<string, unknown>): ClaimsSearch => ({
    status:
      search.status === "closed" || search.status === "opened"
        ? search.status
        : "opened",
    accountId:
      typeof search.accountId === "string" ? search.accountId : undefined,
    provider:
      search.provider === "mercadolivre" || search.provider === "shopee"
        ? search.provider
        : undefined,
    open: typeof search.open === "string" ? search.open : undefined,
    q: typeof search.q === "string" && search.q.trim() ? search.q : undefined,
  }),
  component: ClaimsPage,
});

function ClaimsPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);

  const accounts = useQuery({
    queryKey: ["ml-accounts"],
    queryFn: async () => unwrap(await api.ml.accounts.get()),
  });
  const channels = useChannels();

  const inbox = useQuery({
    queryKey: ["claims", search],
    queryFn: async () =>
      unwrap(
        await api.claims.get({
          query: {
            status: search.status,
            accountId: search.accountId,
            provider: search.provider,
          },
        }),
      ),
  });

  const allClaims = inbox.data?.claims ?? [];
  const claims = allClaims.filter((row) =>
    matchesListQuery(search.q, [
      row.buyerName,
      row.buyerNickname,
      row.externalOrderId,
    ]),
  );
  const writesEnabled = inbox.data?.writesEnabled ?? false;
  const selected = claims.find((row) => row.id === search.open) ?? null;

  function closeThread() {
    void navigate({
      search: (prev) => ({ ...prev, open: undefined }),
    });
  }

  return (
    <>
      <PageHeader
        title="Reclamações"
        description={`${claims.length} reclamaç${claims.length === 1 ? "ão" : "ões"} ${search.status === "opened" ? "abertas" : "neste filtro"}`}
        actions={
          <>
            <ListSearch
              value={search.q ?? ""}
              placeholder="Comprador ou pedido"
              onValueChange={(value) =>
                void navigate({
                  search: (prev) => ({
                    ...prev,
                    q: value.trim() || undefined,
                  }),
                })
              }
            />
            <Select
              value={search.status ?? "opened"}
              itemToStringLabel={(value) =>
                value === "closed" ? "Encerradas" : "Abertas"
              }
              onValueChange={(value) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    status: (value as "opened" | "closed") ?? "opened",
                  }),
                })
              }
            >
              <SelectTrigger />
              <SelectContent>
                <SelectItem value="opened">Abertas</SelectItem>
                <SelectItem value="closed">Encerradas</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={search.accountId ?? null}
              itemToStringLabel={(value) =>
                value == null
                  ? "Todas as contas"
                  : (accounts.data?.accounts.find((account) => account.id === value)
                      ?.displayName ?? String(value))
              }
              onValueChange={(value) =>
                navigate({
                  search: (prev) => ({
                    ...prev,
                    accountId: (value as string) ?? undefined,
                  }),
                })
              }
            >
              <SelectTrigger placeholder="Todas as contas" />
              <SelectContent>
                <SelectItem value={null}>Todas as contas</SelectItem>
                {accounts.data?.accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      />

      <ChannelFilter
        className="mb-3"
        value={search.provider}
        channels={channels.providers.map((provider) => ({ provider }))}
        onChange={(provider) =>
          void navigate({
            search: (prev) => ({
              ...prev,
              provider:
                provider === "mercadolivre" || provider === "shopee"
                  ? provider
                  : undefined,
            }),
          })
        }
      />

      {inbox.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner className="size-5 text-ink-muted" />
        </div>
      ) : allClaims.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Scale />}
            title="Nenhuma reclamação"
            description="Importe as reclamações em Conexões ou aguarde o aviso do canal."
          />
        </Card>
      ) : claims.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Scale />}
            title="Nenhuma reclamação nesta busca"
            description="Tente o nome do comprador ou o código do pedido."
          />
        </Card>
      ) : (
        <Card>
          <ul className="divide-y divide-line">
            {claims.map((row) => {
              const open = openId === row.id;
              return (
                <li key={row.id}>
                  <button
                    type="button"
                    className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left active:scale-[0.99] [@media(hover:hover)_and_(pointer:fine)]:hover:bg-surface-sunken"
                    onClick={() => {
                      if (isNarrowViewport()) {
                        void navigate({
                          search: (prev) => ({ ...prev, open: row.id }),
                        });
                        return;
                      }
                      setOpenId((current) =>
                        current === row.id ? null : row.id,
                      );
                    }}
                  >
                    <ClaimPreview row={row} />
                  </button>
                  {open ? (
                    <div className="hidden border-t border-line bg-surface-sunken/40 p-4 md:block">
                      {row.orderId ? (
                        <div className="mb-3">
                          <Link
                            to="/orders/$id"
                            params={{ id: row.orderId }}
                            className="text-2xs font-medium text-ember-strong hover:underline"
                          >
                            Abrir pedido #{row.externalOrderId ?? "…"}
                          </Link>
                        </div>
                      ) : null}
                      {row.accountProvider === "shopee" ? (
                        <ShopeeClaimActions
                          claimId={row.id}
                          actions={row.availableActions}
                          onDone={() => {
                            void queryClient.invalidateQueries({
                              queryKey: ["claims"],
                            });
                          }}
                        />
                      ) : null}
                      <ClaimBody
                        claimId={row.id}
                        canReply={row.status === "opened"}
                        writesEnabled={writesEnabled}
                        provider={row.accountProvider}
                        onSent={() => {
                          void queryClient.invalidateQueries({
                            queryKey: ["claims"],
                          });
                        }}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      <MobileInboxPane
        open={Boolean(search.open)}
        title={
          selected
            ? (CLAIM_TYPE_LABELS[selected.type] ?? selected.type)
            : "Reclamação"
        }
        subtitle={
          selected?.orderId ? (
            <Link
              to="/orders/$id"
              params={{ id: selected.orderId }}
              className="text-ember-strong"
            >
              Pedido #{selected.externalOrderId ?? "…"}
            </Link>
          ) : (
            selected?.buyerName ?? selected?.buyerNickname
          )
        }
        onClose={closeThread}
      >
        {selected ? (
          <ClaimBody
            claimId={selected.id}
            canReply={selected.status === "opened"}
            writesEnabled={writesEnabled}
            provider={selected.accountProvider}
            variant="sheet"
            onSent={() => {
              void queryClient.invalidateQueries({
                queryKey: ["claims"],
              });
            }}
          />
        ) : (
          <p className="px-4 py-6 text-2xs text-ink-muted">
            Esta reclamação não está neste filtro.
          </p>
        )}
      </MobileInboxPane>
    </>
  );
}

function ShopeeClaimActions({
  claimId,
  actions,
  onDone,
}: {
  claimId: string;
  actions: string[];
  onDone: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState("");
  async function run(action: "confirm" | "dispute") {
    try {
      unwrap(
        await api.claims({ id: claimId }).action.post({
          action,
          reason: reason.trim() || undefined,
        }),
      );
      toast.add({
        type: "success",
        title: action === "confirm" ? "Devolução aceita" : "Contestação enviada",
      });
      onDone();
    } catch (error) {
      toast.add({
        type: "error",
        title: "A Shopee não concluiu a ação",
        description: error instanceof Error ? error.message : "Tente de novo.",
      });
    }
  }
  if (!actions.includes("confirm") && !actions.includes("dispute")) return null;
  return (
    <div className="mb-3 flex flex-col gap-2">
      {actions.includes("dispute") ? (
        <input
          value={reason}
          placeholder="Motivo da contestação"
          onChange={(event) => setReason(event.target.value)}
          className="h-8 rounded-control border border-line bg-surface px-2 text-2xs text-ink"
        />
      ) : null}
      <div className="flex gap-2">
        {actions.includes("confirm") ? (
          <Button size="sm" variant="secondary" onClick={() => void run("confirm")}>
            Aceitar devolução
          </Button>
        ) : null}
        {actions.includes("dispute") ? (
          <Button size="sm" variant="secondary" onClick={() => void run("dispute")}>
            Contestar
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function dueBadge(dueAt: string | Date | null) {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  const hours = (due.getTime() - Date.now()) / 36e5;
  if (hours < 0) {
    return (
      <Badge variant="danger" dot>
        Prazo vencido
      </Badge>
    );
  }
  if (hours <= 24) {
    return (
      <Badge variant="warning" dot>
        Prazo em 24h
      </Badge>
    );
  }
  return (
    <span className="text-2xs text-ink-muted">
      até {formatDateTime(dueAt)}
    </span>
  );
}

function ClaimPreview({
  row,
}: {
  row: {
    type: string;
    status: string;
    reasonId: string | null;
    hasReturn: boolean;
    returnStatus: string | null;
    dueAt: string | Date | null;
    buyerName: string | null;
    buyerNickname?: string | null;
    externalOrderId: string | null;
    accountNickname: string;
    accountProvider?: string;
    lastUpdatedAt: string | Date;
  };
}) {
  return (
    <>
      <div className="min-w-0 flex-1">
        <p className="text-[0.8125rem] font-medium text-ink">
          {CLAIM_TYPE_LABELS[row.type] ?? row.type}
          {row.reasonId ? (
            <span className="ml-1.5 font-mono text-2xs font-normal text-ink-muted">
              {row.reasonId}
            </span>
          ) : null}
        </p>
        <p className="mt-0.5 text-2xs text-ink-muted">
          {row.externalOrderId
            ? `#${row.externalOrderId}`
            : "Pedido ainda sem vínculo"}
          {" · "}
          {row.buyerName ?? row.buyerNickname ?? "Comprador"}
        </p>
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 text-2xs text-ink-faint">
          {row.accountProvider ? (
            <ChannelBadge
              provider={row.accountProvider}
              nickname={row.accountNickname}
            />
          ) : (
            row.accountNickname
          )}
          <span>
            · {formatDateTime(row.lastUpdatedAt)}
            {row.hasReturn
              ? ` · devolução${row.returnStatus ? ` ${row.returnStatus}` : ""}`
              : ""}
          </span>
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <Badge variant={CLAIM_STATUS_VARIANTS[row.status] ?? "neutral"} dot>
          {CLAIM_STATUS_LABELS[row.status] ?? row.status}
        </Badge>
        {row.status === "opened" ? dueBadge(row.dueAt) : null}
      </div>
    </>
  );
}

function ClaimBody({
  claimId,
  canReply,
  writesEnabled,
  provider,
  onSent,
  variant = "inline",
}: {
  claimId: string;
  canReply: boolean;
  writesEnabled: boolean;
  provider?: string;
  onSent: () => void;
  variant?: "inline" | "sheet";
}) {
  const detail = useQuery({
    queryKey: ["claims", claimId],
    queryFn: async () => unwrap(await api.claims({ id: claimId }).get()),
  });

  if (detail.isLoading || !detail.data || !("claim" in detail.data)) {
    return (
      <div className="flex justify-center py-6">
        <Spinner className="size-4 text-ink-muted" />
      </div>
    );
  }

  const stockReturned =
    "stockReturned" in detail.data && Boolean(detail.data.stockReturned);

  return (
    <div
      className={
        variant === "sheet"
          ? "flex h-full min-h-0 flex-col"
          : "flex flex-col gap-3"
      }
    >
      {detail.data.claim.hasReturn ? (
        <div className={variant === "sheet" ? "shrink-0 px-4 pt-3" : undefined}>
          <ClaimReturnButton
            claimId={claimId}
            hasReturn
            stockReturned={stockReturned}
            onDone={onSent}
          />
        </div>
      ) : null}
      {provider === "shopee" ? (
        <ChannelNotice provider="shopee" className="mx-4">
          A Shopee resolve devoluções sem chat; converse pelo módulo Mensagens.
        </ChannelNotice>
      ) : (
      <ClaimThread
        variant={variant}
        className={variant === "sheet" ? "h-auto min-h-0 flex-1" : undefined}
        claimId={claimId}
        messages={detail.data.messages}
        canReply={
          "canReply" in detail.data
            ? Boolean(detail.data.canReply)
            : canReply
        }
        writesEnabled={
          "writesEnabled" in detail.data
            ? Boolean(detail.data.writesEnabled)
            : writesEnabled
        }
        vars={replyVarsFromOrder(detail.data.order ?? {})}
        onSent={onSent}
      />
      )}
    </div>
  );
}
